import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';

import {Button, Checkbox, Page, Tile} from '../../../../shared';
import Table from '../../../../shared/components/Table';
import {gatherFormattedData} from '../../../../shared/utils/utils';
import {_openModal} from '../../../../shared/reducers/modals';
import axiosInstance from '../../../../shared/utils/axios';

const HouseholdBilling = props => {
  const [householdOriginals, setHouseholdOriginals] = useState({});
  const [accountOriginals, setAccountOriginals] = useState({});
  const [householdUpdates, setHouseholdUpdates] = useState({});
  const [accountUpdates, setAccountUpdates] = useState({});
  const [householdAccountMap, setHouseholdAccountMap] = useState({});
  const [accountHouseholdMap, setAccountHouseholdMap] = useState({});
  const [selectedHouseholdID, setSelectedHouseholdID] = useState(null);
  const [drilldownRowID, setDrilldownRowID] = useState(null);

  const gatherHouseholds = gatherFormattedData(
    '/households',
    ['amount'],
    {amount: 'currency'}
  );

  const gatherAccounts = householdID => gatherFormattedData(
    '/crm/accounts?householdID=' + householdID,
    ['balance'],
    {balance: 'currency'}
  );

  useEffect(() => {
    let fetchedHouseholds = false;

    if (fetchedHouseholds) {return;}

    gatherHouseholds({}).then(households => {
      if (fetchedHouseholds) {return;}

      const initialHouseholdIncludeForBilling = {};

      for (let household of households) {
        initialHouseholdIncludeForBilling[String(household.id)] = household.includeForBilling;
      }

      setHouseholdOriginals(initialHouseholdIncludeForBilling);
    });

    return () => {
      fetchedHouseholds = true;
    };
  }, []);

  const householdDataManager = {
    gatherData: inputValue => gatherHouseholds({...inputValue}),
  };

  const accountDataManager = {
    gatherData: inputValue => gatherAccounts(selectedHouseholdID)({...inputValue}),
  };

  const changeAccountUpdates = (householdsToAccounts, householdId, includeForBilling) => {
    const householdAccountUpdates = {};

    for (let account of householdsToAccounts[householdId]) {
      householdAccountUpdates[account] = includeForBilling;
    }

    setAccountUpdates({
      ...accountUpdates,
      ...householdAccountUpdates,
    });
  };

  const onHouseholdCheckboxChange = (name, value) => {
    setHouseholdUpdates({
      ...householdUpdates,
      [name]: value,
    });

    const accountIncludeForBilling = {...accountOriginals};
    const householdsToAccounts = {...householdAccountMap};
    const accountsToHouseholds = {...accountHouseholdMap};

    if (!householdsToAccounts[name]) {
      householdsToAccounts[name] = [];

      gatherAccounts(name)().then(accounts => {
        for (let account of accounts) {
          accountIncludeForBilling[String(account.id)] = account.includeForBilling;
          householdsToAccounts[name].push(String(account.id));
          accountsToHouseholds[String(account.id)] = name;
        }

        setAccountOriginals(accountIncludeForBilling);
        setHouseholdAccountMap(householdsToAccounts);
        setAccountHouseholdMap(accountsToHouseholds);

        changeAccountUpdates(householdsToAccounts, name, value);
      });
    } else {
      changeAccountUpdates(householdsToAccounts, name, value);
    }
  };

  const onAccountCheckboxChange = (name, value) => {
    setAccountUpdates({
      ...accountUpdates,
      [name]: value,
    });
  };

  const householdIncludeForBillingRender = household => {
    const updatedValue = householdUpdates.hasOwnProperty(String(household.id))
      ? householdUpdates[String(household.id)] : householdOriginals[String(household.id)];

    return (
      <span onClick={e => e.stopPropagation()}>
        <Checkbox
          dataPoint={{name: String(household.id), value: !!updatedValue}}
          onChange={onHouseholdCheckboxChange}
        />
      </span>
    );
  };

  const accountIncludeForBillingRender = account => {
    const updatedValue = accountUpdates.hasOwnProperty(String(account.id))
      ? accountUpdates[String(account.id)] : accountOriginals[String(account.id)];

    const householdID = accountHouseholdMap[String(account.id)];
    const householdChecked = householdUpdates.hasOwnProperty(householdID)
      ? householdUpdates[householdID] : householdOriginals[householdID];

    return (
      <span onClick={e => e.stopPropagation()}>
        <Checkbox
          dataPoint={{name: String(account.id), value: !!updatedValue}}
          onChange={onAccountCheckboxChange}
          disabled={!householdChecked}
        />
      </span>
    );
  };

  const expandHouseholdAccounts = householdRow => {
    setSelectedHouseholdID(householdRow.id);
    setDrilldownRowID(householdRow.id);

    if (householdAccountMap[String(householdRow.id)]) {return;}

    const accountIncludeForBilling = {...accountOriginals};
    const householdsToAccounts = {...householdAccountMap};
    const accountsToHouseholds = {...accountHouseholdMap};

    householdsToAccounts[String(householdRow.id)] = [];

    gatherAccounts(householdRow.id)().then(accounts => {
      for (let account of accounts) {
        accountIncludeForBilling[String(account.id)] = account.includeForBilling;
        householdsToAccounts[String(householdRow.id)].push(String(account.id));
        accountsToHouseholds[String(account.id)] = String(householdRow.id);
      }

      setAccountOriginals(accountIncludeForBilling);
      setHouseholdAccountMap(householdsToAccounts);
      setAccountHouseholdMap(accountsToHouseholds);
    });
  };

  const confirmSave = data => {
    axiosInstance.put('/accounts/billing', data)
      .then(() => {
        props.openModal({
          type: 'MessageModal',
          title: 'Changes Saved',
          body: 'Your changes have been saved and submitted to our billing service.',
        });
      })
      .catch(err => {
        props.openModal({
          type: 'MessageModal',
          title: 'Confirm Changes',
          body: err && err.data && err.data.error && err.data.error.msg,
        });
      });
  };

  const saveChanges = () => {
    let message = 'The changes you have made will be submitted to our billing service for processing and will be '
      + 'available for invoicing on the next business day. You can adjust your selection and re-save at any time '
      + 'and the changes will be reflected on the following business day.';

    const householdIDs = Object.keys(householdUpdates);
    const accountIDs = Object.keys(accountUpdates);
    let foundExclusion = false;

    // Look for new exclusion
    for (let householdID of householdIDs) {
      if (!householdUpdates[householdID] && householdOriginals[householdID]) {
        foundExclusion = true;
        break;
      }
    }

    if (!foundExclusion) {
      for (let accountID of accountIDs) {
        if (!accountUpdates[accountID] && accountOriginals[accountID]) {
          foundExclusion = true;
          break;
        }
      }
    }

    if (foundExclusion) {
      message += '\n\nYour changes include the removal of previously processed accounts. As of the next billing '
        + 'period these accounts and any existing invoices that include them will be removed from our '
        + 'billing service. (A record of the invoices will still exist in your Document Vault.) Are you sure you want to make these changes?';
    }

    const body = {
      households: householdIDs.map(householdID => (
        {id: householdID, includeForBilling: householdUpdates[householdID]}
      )),
      accounts: accountIDs.map(accountID => (
        {id: accountID, includeForBilling: accountUpdates[accountID]}
      )),
    };

    props.openModal({
      type: 'ConfirmModal',
      title: 'Confirm Changes',
      body: message,
      button: {
        action: confirmSave,
        data: body,
      },
    });
  };

  let accountTable = <Table
    dataManager={accountDataManager}
    columns={[
      {
        title: 'Account',
        dataPoint: 'name',
        type: 'text',
        minWidth: 160,
        isSortable: true,
      },
      {
        title: 'Account #',
        dataPoint: 'number',
        type: 'text',
        minWidth: 60,
        isSortable: true,
      },
      {
        title: 'Type',
        dataPoint: 'typeName',
        type: 'text',
        minWidth: 60,
        isSortable: true,
      },
      {
        title: 'Institution',
        dataPoint: 'institutionName',
        type: 'text',
        minWidth: 60,
        isSortable: true,
      },
      {
        title: 'Value',
        dataPoint: 'balance',
        type: 'text',
        minWidth: 60,
        isSortable: true,
      },
      {
        title: 'Include For Billing',
        dataPoint: 'isIncludedForBilling',
        render: accountIncludeForBillingRender,
        type: 'custom',
        minWidth: 60,
        isSortable: false,
      },
    ]}
    defaultSort={{
      column: 'name',
      desc: false,
    }}
    searches={[
      {
        title: 'Accounts',
        name: 'name',
        isFuzzy: true,
      },
    ]}
  />;

  let householdTable = (
    <Table
      dataManager={householdDataManager}
      columns={[
        {
          title: 'Households',
          dataPoint: 'name',
          type: 'text',
          minWidth: 160,
          isSortable: true,
        },
        {
          title: 'Assets',
          dataPoint: 'amount',
          type: 'text',
          minWidth: 60,
          isSortable: true,
        },
        {
          title: 'Include For Billing',
          dataPoint: 'isIncludedForBilling',
          render: householdIncludeForBillingRender,
          type: 'custom',
          minWidth: 60,
          isSortable: false,
        },
      ]}
      defaultSort={{
        column: 'name',
        desc: false,
      }}
      searches={[
        {
          title: 'Households',
          name: 'name',
          isFuzzy: true,
        },
      ]}
      rowOnClick={expandHouseholdAccounts}
      rowIdentifier={'id'}
      subContent={{
        backButtonLabel: 'Back',
        content: accountTable,
        rowID: drilldownRowID,
        setRow: id => setDrilldownRowID(id),
      }}
    />
  );

  householdTable = Object.keys(householdOriginals).length ? householdTable : <div>No households to display.</div>;

  return (
    <Page>
      <Tile key={'Household Billing'} title={'Household Billing'}>
        <Button importance={'secondary'}
                size={'md'}
                label={'Save Changes'}
                disabled={!(Object.keys(householdUpdates).length || Object.keys(accountUpdates).length)}
                action={saveChanges}
        />
        {householdTable}
      </Tile>
    </Page>
  );
};

const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(null, mapDispatchToProps)(HouseholdBilling);

HouseholdBilling.propTypes = {
  /**
   * The open function that dispatches _openModal
   */
  openModal: PropTypes.func.isRequired,
};
